import { supabase } from '@/lib/supabase';
import { Sale, SalePayment, PaginatedResult } from '@/types/models';
import { PAGE_SIZE } from '@/lib/constants';

export interface FetchSalesParams {
  search?: string;
  customerId?: string;
  status?: string;
  page?: number;
}

export async function fetchSales(
  params: FetchSalesParams
): Promise<PaginatedResult<Sale>> {
  const { search = '', customerId = '', status = '', page = 0 } = params;
  const from = page * PAGE_SIZE;
  const to = from + PAGE_SIZE - 1;

  let query = supabase
    .from('sales')
    .select('*, customer:customers(id, name, phone)', { count: 'exact' })
    .order('sale_date', { ascending: false })
    .range(from, to);

  if (search.trim()) {
    const s = search.trim();
    query = query.or(`invoice_number.ilike.%${s}%,notes.ilike.%${s}%`);
  }
  if (customerId) query = query.eq('customer_id', customerId);
  if (status) query = query.eq('payment_status', status);

  const { data, error, count } = await query;
  if (error) throw error;
  return { data: (data ?? []) as Sale[], count: count ?? 0 };
}

export async function fetchSaleById(id: string): Promise<Sale> {
  const { data, error } = await supabase
    .from('sales')
    .select('*, customer:customers(id, name, phone, email, address), items:sale_items(*, laptop:laptops(id, stock_id, brand, model, serial_number), accessory:accessories(id, sku, name))')
    .eq('id', id)
    .single();

  if (error) throw error;
  return data as Sale;
}

export async function fetchSalePayments(saleId: string): Promise<SalePayment[]> {
  const { data, error } = await supabase
    .from('payments')
    .select('*')
    .eq('sale_id', saleId)
    .order('payment_date', { ascending: false });

  if (error) return [];
  return (data ?? []) as SalePayment[];
}

/**
 * complete_sale — exact RPC args from SQL:
 * p_customer_id, p_items, p_discount, p_tax, p_payment_amount, p_payment_method, p_notes, p_warranty_months, p_walk_in_confirmed
 *
 * p_items JSON keys read by the function body:
 *   type (laptop|accessory|service)
 *   id — laptop or accessory uuid
 *   unit_price — override price (optional; falls back to asking_price or selling_price)
 *   unit_cost — for service lines only
 *   line_discount
 *   quantity — for accessory/service; laptops always qty=1
 *   name — for service lines
 *   complimentary — boolean (explicit complimentary line: unit_price=0, line_discount=0)
 */
export interface LaptopComponentAdjustmentPayload {
  component_type: 'ram' | 'storage';
  direction: 'installed' | 'recovered';
  accessory_id: string;
  quantity: number;
  unit_cost?: number; // Explicit unit cost for recovered parts
}

export interface CompleteSalePayload {
  customer_id: string | null;
  walk_in_confirmed?: boolean;
  items: Array<
    | {
        type: 'laptop';
        id: string;
        unit_price?: number | null;
        line_discount?: number;
        ram?: string | null;
        storage?: string | null;
        clear_ram?: boolean;
        clear_storage?: boolean;
        complimentary?: boolean;
        component_adjustments?: LaptopComponentAdjustmentPayload[];
      }
    | {
        type: 'accessory';
        id: string;
        quantity: number;
        unit_price?: number | null;
        line_discount?: number;
        complimentary?: boolean;
      }
    | {
        type: 'service';
        name: string;
        quantity?: number;
        unit_price: number;
        unit_cost?: number;
        line_discount?: number;
        complimentary?: boolean;
      }
  >;
  discount?: number;
  tax?: number;
  payment_amount?: number | null;
  payment_method?: string;
  notes?: string | null;
  warranty_months?: number | null;
}

export async function completeSaleRpc(payload: CompleteSalePayload): Promise<{
  sale_id: string;
  invoice_number: string;
  subtotal: number;
  total: number;
  amount_paid: number;
  balance_due: number;
}> {
  const { data, error } = await supabase.rpc('complete_sale', {
    p_customer_id: payload.customer_id,
    p_items: payload.items,
    p_discount: payload.discount ?? 0,
    p_tax: payload.tax ?? 0,
    p_payment_amount: payload.payment_amount ?? null,
    p_payment_method: payload.payment_method ?? 'cash',
    p_notes: payload.notes ?? null,
    p_warranty_months: payload.warranty_months ?? null,
    p_walk_in_confirmed: payload.walk_in_confirmed ?? false,
  });

  if (error) throw error;
  return data as { sale_id: string; invoice_number: string; subtotal: number; total: number; amount_paid: number; balance_due: number };
}

/**
 * record_sale_payment — exact RPC args from SQL:
 * p_sale_id, p_amount, p_payment_method, p_reference, p_notes
 */
export async function recordSalePaymentRpc(params: {
  sale_id: string;
  amount: number;
  payment_method: string;
  reference?: string;
  notes?: string;
}): Promise<void> {
  const { error } = await supabase.rpc('record_sale_payment', {
    p_sale_id: params.sale_id,
    p_amount: params.amount,
    p_payment_method: params.payment_method,
    p_reference: params.reference ?? null,
    p_notes: params.notes ?? null,
  });

  if (error) throw error;
}

export interface CorrectSaleInvoiceParams {
  sale_id: string;
  reason: string;
  customer_id?: string | null;
  notes?: string | null;
  warranty_months?: number | null;
  discount?: number | null;
  tax?: number | null;
  item_corrections?: Array<{
    sale_item_id: string;
    unit_price?: number | null;
    line_discount?: number | null;
    ram_snapshot?: string | null;
    storage_snapshot?: string | null;
  }> | null;
  change_to_walk_in?: boolean;
}

export async function correctSaleInvoiceRpc(params: CorrectSaleInvoiceParams): Promise<{
  success: boolean;
  sale_id: string;
  invoice_number: string;
  subtotal: number;
  total: number;
  balance_due: number;
  payment_status: string;
}> {
  const { data, error } = await supabase.rpc('correct_sale_invoice', {
    p_sale_id: params.sale_id,
    p_reason: params.reason,
    p_customer_id: params.customer_id ?? null,
    p_notes: params.notes ?? null,
    p_warranty_months: params.warranty_months ?? null,
    p_discount: params.discount ?? null,
    p_tax: params.tax ?? null,
    p_item_corrections: params.item_corrections ?? null,
    p_change_to_walk_in: params.change_to_walk_in ?? false,
  });

  if (error) throw error;
  return data as {
    success: boolean;
    sale_id: string;
    invoice_number: string;
    subtotal: number;
    total: number;
    balance_due: number;
    payment_status: string;
  };
}

export interface CorrectSalePaymentEntryParams {
  payment_id: string;
  new_amount: number;
  reason: string;
  new_payment_method?: string | null;
  new_reference?: string | null;
  new_notes?: string | null;
}

export async function correctSalePaymentEntryRpc(params: CorrectSalePaymentEntryParams): Promise<{
  success: boolean;
  payment_id: string;
  sale_id: string;
  old_amount: number;
  new_amount: number;
  new_balance_due: number;
  new_payment_status: string;
}> {
  const { data, error } = await supabase.rpc('correct_sale_payment_entry', {
    p_payment_id: params.payment_id,
    p_new_amount: params.new_amount,
    p_reason: params.reason,
    p_new_payment_method: params.new_payment_method ?? null,
    p_new_reference: params.new_reference ?? null,
    p_new_notes: params.new_notes ?? null,
  });

  if (error) throw error;
  return data as {
    success: boolean;
    payment_id: string;
    sale_id: string;
    old_amount: number;
    new_amount: number;
    new_balance_due: number;
    new_payment_status: string;
  };
}

export interface GrantPostSaleDiscountRefundParams {
  sale_id: string;
  new_discount: number;
  refund_amount: number;
  refund_method: string;
  reason: string;
  reference?: string | null;
  notes?: string | null;
}

export async function grantPostSaleDiscountRefundRpc(params: GrantPostSaleDiscountRefundParams): Promise<{
  success: boolean;
  sale_id: string;
  payment_id: string;
  invoice_number: string;
  new_discount: number;
  new_total: number;
  refund_amount: number;
  balance_due: number;
  payment_status: string;
}> {
  const { data, error } = await supabase.rpc('grant_post_sale_discount_refund', {
    p_sale_id: params.sale_id,
    p_new_discount: params.new_discount,
    p_refund_amount: params.refund_amount,
    p_refund_method: params.refund_method,
    p_reason: params.reason,
    p_reference: params.reference ?? null,
    p_notes: params.notes ?? null,
  });

  if (error) throw error;
  return data as {
    success: boolean;
    sale_id: string;
    payment_id: string;
    invoice_number: string;
    new_discount: number;
    new_total: number;
    refund_amount: number;
    balance_due: number;
    payment_status: string;
  };
}

export interface LegacySaleCorrectionPreviewItem {
  sale_item_id: string;
  item_type: string;
  item_name: string;
  laptop_id: string | null;
  stock_id: string | null;
  serial_snapshot: string | null;
  ram_snapshot: string | null;
  storage_snapshot: string | null;
  specs_snapshot: string | null;
  quantity: number;
  unit_price: number;
  unit_cost: number;
  base_unit_cost_snapshot: number | null;
  configuration_cost_adjustment: number;
  line_discount: number;
  line_total: number;
  line_cost: number;
  line_profit: number;
  laptop_purchase_price: number | null;
  laptop_landed_cost: number | null;
  laptop_current_cost_basis: number | null;
  original_specs?: {
    ram: string | null;
    storage: string | null;
    source: 'inventory_creation_audit' | 'earliest_inventory_audit' | 'not_recorded';
    recorded_at?: string;
    audit_id?: number;
    uncertain: boolean;
  };
  laptop_hw_ram: string | null;
  laptop_hw_storage: string | null;
  laptop_status: string | null;
  has_returns: boolean;
  is_restocked?: boolean;
  cost_provenance?: string;
  evidence_uncertain?: boolean;
  proposed_base_cost: number;
  proposed_cogs: number;
  proposed_profit: number;
  inventory_reconciliation_needed: boolean;
}

export interface LegacySaleCorrectionPreviewResponse {
  success: boolean;
  sale_id: string;
  invoice_number: string;
  sale_date: string;
  subtotal: number;
  discount: number;
  tax: number;
  total: number;
  amount_paid: number;
  balance_due: number;
  items: LegacySaleCorrectionPreviewItem[];
}

export async function previewLegacySaleCorrectionRpc(
  saleId: string
): Promise<LegacySaleCorrectionPreviewResponse> {
  const { data, error } = await supabase.rpc('preview_legacy_sale_correction', {
    p_sale_id: saleId,
  });

  if (error) throw error;
  return data as LegacySaleCorrectionPreviewResponse;
}

export interface ApplyLegacySaleCorrectionParams {
  sale_id: string;
  sale_item_id: string;
  reason: string;
  expected_unit_cost: number;
  expected_config_adj?: number;
  new_unit_cost: number;
  new_base_unit_cost?: number | null;
  new_config_adj?: number;
  new_ram?: string | null;
  new_storage?: string | null;
}

export async function applyLegacySaleCorrectionRpc(
  params: ApplyLegacySaleCorrectionParams
): Promise<{
  success: boolean;
  sale_id: string;
  sale_item_id: string;
  old_unit_cost: number;
  new_unit_cost: number;
  new_line_cost: number;
  new_line_profit: number;
  new_ram: string | null;
  new_storage: string | null;
  specs_snapshot: string;
}> {
  const { data, error } = await supabase.rpc('apply_legacy_sale_correction', {
    p_sale_id: params.sale_id,
    p_sale_item_id: params.sale_item_id,
    p_reason: params.reason,
    p_expected_unit_cost: params.expected_unit_cost,
    p_expected_config_adj: params.expected_config_adj ?? 0,
    p_new_unit_cost: params.new_unit_cost,
    p_new_base_unit_cost: params.new_base_unit_cost ?? null,
    p_new_config_adj: params.new_config_adj ?? 0,
    p_new_ram: params.new_ram ?? null,
    p_new_storage: params.new_storage ?? null,
  });

  if (error) throw error;
  return data as any;
}

export async function fetchCustomers(search = ''): Promise<Array<{ id: string; name: string; phone: string | null; email?: string | null; address?: string | null }>> {
  let query = supabase
    .from('customers')
    .select('id, name, phone, email, address')
    .eq('is_active', true)
    .order('name')
    .limit(50);

  if (search.trim()) {
    const s = search.trim();
    query = query.or(`name.ilike.%${s}%,phone.ilike.%${s}%,email.ilike.%${s}%`);
  }

  const { data, error } = await query;
  if (error) throw error;
  return (data ?? []) as Array<{ id: string; name: string; phone: string | null; email?: string | null; address?: string | null }>;
}

export async function fetchCustomerByExactPhoneOrEmail(params: { phone?: string | null; email?: string | null }): Promise<{ id: string; name: string; phone: string | null; email: string | null } | null> {
  const { phone, email } = params;
  if (!phone?.trim() && !email?.trim()) return null;

  let query = supabase.from('customers').select('id, name, phone, email').eq('is_active', true);
  const filters: string[] = [];
  if (phone?.trim()) filters.push(`phone.eq.${phone.trim()}`);
  if (email?.trim()) filters.push(`email.ilike.${email.trim()}`);

  query = query.or(filters.join(',')).limit(1);
  const { data, error } = await query;
  if (error || !data || data.length === 0) return null;
  return data[0] as { id: string; name: string; phone: string | null; email: string | null };
}

export interface QuickCustomerPayload {
  name: string;
  phone?: string | null;
  whatsapp?: string | null;
  email?: string | null;
  address?: string | null;
  notes?: string | null;
}

export async function quickCreateCustomer(payload: QuickCustomerPayload): Promise<{ id: string; name: string; phone: string | null; email: string | null; address: string | null; whatsapp?: string | null }> {
  const { data, error } = await supabase
    .from('customers')
    .insert({
      name: payload.name.trim(),
      phone: payload.phone?.trim() || null,
      whatsapp: payload.whatsapp?.trim() || null,
      email: payload.email?.trim() || null,
      address: payload.address?.trim() || null,
      notes: payload.notes?.trim() || null,
      is_active: true,
    })
    .select('id, name, phone, email, address, whatsapp')
    .single();

  if (error) throw error;
  return data as { id: string; name: string; phone: string | null; email: string | null; address: string | null; whatsapp?: string | null };
}
